import { useEffect, useState } from "react";
import { apiFetch } from "../lib/apiFetch";
import type { PlanYears } from "../types/api";

function isPlanYears(body: unknown): body is PlanYears {
  return (
    typeof body === "object" &&
    body !== null &&
    "current" in body &&
    typeof body.current === "number" &&
    "next" in body &&
    (typeof body.next === "number" || body.next === null) &&
    "nextOopThreshold" in body &&
    (typeof body.nextOopThreshold === "number" || body.nextOopThreshold === null)
  );
}

/**
 * The plan years the plan lists and estimates answer for, from the public
 * `GET /api/plans/years`, fetched once per mount.
 *
 * Null while the request is in flight. A failure of any kind (network, non-2xx,
 * an unexpected body) resolves to `next: null`, so a toggle keyed on `next`
 * simply doesn't render; its `current` is then the client clock's year, which
 * nothing displays. `baseUrl` is the SPA's `VITE_API_BASE_URL` (see `apiFetch`).
 */
export function usePlanYears(baseUrl: string, credentials?: RequestCredentials): PlanYears | null {
  const [years, setYears] = useState<PlanYears | null>(null);
  useEffect(() => {
    let cancelled = false;
    const settle = (body: unknown): void => {
      if (cancelled) return;
      setYears(
        isPlanYears(body)
          ? body
          : { current: new Date().getFullYear(), next: null, nextOopThreshold: null },
      );
    };
    apiFetch<unknown>("/api/plans/years", credentials ? { credentials } : {}, { baseUrl })
      .then(settle)
      .catch(() => settle(null));
    return () => {
      cancelled = true;
    };
  }, [baseUrl, credentials]);
  return years;
}
